import React, { useState, useCallback, useEffect } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import Card from '@material-ui/core/Card'
import CardContent from '@material-ui/core/CardContent'
import Collapse from '@material-ui/core/Collapse'
import IconButton from '@material-ui/core/IconButton'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import Typography from '@material-ui/core/Typography'
import TextField from '@material-ui/core/TextField'
import InputAdornment from '@material-ui/core/InputAdornment'
import SearchIcon from '@material-ui/icons/Search'
import ClearIcon from '@material-ui/icons/Clear'
import CircularProgress from '@material-ui/core/CircularProgress'
import MaterialTable from 'material-table'
import Folder from '@material-ui/icons/Folder'
import WarningIcon from '@material-ui/icons/Warning'
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord'
import Tooltip from '@material-ui/core/Tooltip'
import icons from '../../../utils/icons'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'
import { nl as nlLocale } from 'date-fns/locale'
import { client } from '../../../client'

const useStyles = makeStyles((theme) => ({
  card: {
    marginBottom: theme.spacing(2)
  },
  cardHeader: {
    paddingBottom: theme.spacing(1),
    paddingTop: theme.spacing(1),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1)
  },
  headerContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    width: '100%',
    padding: theme.spacing(1, 0),
    borderRadius: theme.spacing(0.5)
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  sectionIcon: {
    display: 'flex',
    alignItems: 'center',
    '& > *': {
      fontSize: '1.25rem !important',
      color: theme.palette.action.active 
    }
  },
  cardContent: {
    paddingTop: 0,
    '&:last-child': {
      paddingBottom: theme.spacing(2)
    }
  },
  searchField: {
    marginBottom: theme.spacing(2),
    width: '100%',
    maxWidth: 400
  },
  expandIcon: {
    transform: 'rotate(0deg)',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest
    })
  },
  expandIconExpanded: {
    transform: 'rotate(180deg)'
  },
  warningIcon: {
    color: '#FF9800',
    fontSize: '16px',
    marginLeft: theme.spacing(0.5)
  },
  warningTooltip: {
    fontSize: '0.95rem',
    padding: theme.spacing(1, 1.5)
  },
  tableContainer: {
    maxHeight: 400,
    overflow: 'auto'
  },
  scrollableList: {
    maxHeight: 600,
    overflow: 'auto'
  }
}))

interface Patient {
  id: number
  firstName: string
  infix?: string
  lastName: string
  birthDate?: string
  lastModifiedAt: string
  practice?: { name: string }
  language?: string
  onsite?: boolean
  online?: boolean
  practiceName?: string
  intakeWarning?: boolean
  fullName?: string
  shortBirthDate?: string
  formattedLastModifiedAt?: string
}

interface CollapsiblePatientSectionProps {
  title: string
  icon: React.ComponentType
  patients: Patient[]
  totalCount: number
  isExpanded?: boolean
  showPagination?: boolean
  pageSize?: number
  searchEnabled?: boolean
  showIntakeWarnings?: boolean
  loading?: boolean
  onLoadMore?: (query: any) => Promise<any>
  onExpand?: () => Promise<void>
  serverSideSearch?: boolean
  dateColumnTitle?: string
  showSessionsColumn?: boolean
  showSessionDot?: boolean
}

export const CollapsiblePatientSection: React.FC<CollapsiblePatientSectionProps> = ({
  title,
  icon: IconComponent,
  patients,
  totalCount,
  isExpanded = false,
  showPagination = false,
  pageSize = 10,
  searchEnabled = true,
  showIntakeWarnings = false,
  loading = false,
  onLoadMore,
  onExpand,
  serverSideSearch = false,
  dateColumnTitle,
  showSessionsColumn = false,
  showSessionDot = false
}) => {
  const classes = useStyles()
  const [expanded, setExpanded] = useState(isExpanded)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [isSearching, setIsSearching] = useState(false)
  const [tableKey, setTableKey] = useState(0) 
  const [lastSearchTerm, setLastSearchTerm] = useState('') 
  const [isTableVisible, setIsTableVisible] = useState(true) 
  const navigate = useNavigate()
  const { t } = useTranslation('patientSelectPage')

  useEffect(() => {
    if (searchTerm !== debouncedSearchTerm) {
      setIsSearching(true)
    }
    
    const timer = setTimeout(() => {
      const newDebouncedSearchTerm = searchTerm
      setDebouncedSearchTerm(newDebouncedSearchTerm)
      setIsSearching(false)
      
      const searchChanged = newDebouncedSearchTerm !== lastSearchTerm
      
      if (serverSideSearch && showPagination && searchChanged) {
        setIsTableVisible(false)
        setTimeout(() => {
          setTableKey(Date.now()) 
          setLastSearchTerm(newDebouncedSearchTerm)
          setIsTableVisible(true)
        }, 50)
      }
    }, 500)

    return () => clearTimeout(timer)
  }, [searchTerm, debouncedSearchTerm, serverSideSearch, showPagination, lastSearchTerm])

  const handleExpandClick = async () => {
    const newExpanded = !expanded
    setExpanded(newExpanded)
    
    if (newExpanded && onExpand) {
      await onExpand()
    }
  }

  const getFullName = (patient: Patient) => {
    const { firstName, infix, lastName } = patient
    return [firstName, infix, lastName].filter(Boolean).join(' ')
  }

  const isValidDate = (date: any) => {
    if (!date) return false
    
    let d: Date
    
    if (date instanceof Date) {
      d = date
    }
    else if (typeof date === 'number') {
      d = new Date(date)
    }
    else if (typeof date === 'string') {
      if (date.match(/^\d{2}-\d{2}-\d{4}, \d{2}:\d{2}$/)) {
        const [datePart, timePart] = date.split(', ')
        const [day, month, year] = datePart.split('-')
        const [hour, minute] = timePart.split(':')
        d = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), parseInt(hour), parseInt(minute))
      } else {
        d = new Date(date)
      }
    }
    else {
      d = new Date(date)
    }
    
    return d instanceof Date && !isNaN(d.getTime())
  }

  const formatPatientData = useCallback((patientList: Patient[]) => {
    return patientList
      .filter((patient) => {
        if (!debouncedSearchTerm) return true
        const fullName = getFullName(patient).toLowerCase()
        return fullName.includes(debouncedSearchTerm.toLowerCase())
      })
      .map((patient) => {
        return {
          ...patient,
          fullName: getFullName(patient),
          shortBirthDate: (patient as any).startDate && isValidDate((patient as any).startDate)
            ? format(new Date((patient as any).startDate), 'P', { locale: nlLocale })
            : patient.birthDate && isValidDate(patient.birthDate)
            ? format(new Date(patient.birthDate), 'P', { locale: nlLocale })
            : '',
          formattedLastModifiedAt: (() => {
            if (!patient.lastModifiedAt || !isValidDate(patient.lastModifiedAt)) {
              return '-'
            }
            
            let dateObj: Date
            
            const dateValue: any = patient.lastModifiedAt
            if (dateValue instanceof Date) {
              dateObj = dateValue
            } else if (typeof dateValue === 'number') {
              dateObj = new Date(dateValue)
            } else if (typeof dateValue === 'string') {
              if (dateValue.match(/^\d{2}-\d{2}-\d{4}, \d{2}:\d{2}$/)) {
                const [datePart, timePart] = dateValue.split(', ')
                const [day, month, year] = datePart.split('-')
                const [hour, minute] = timePart.split(':')
                dateObj = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), parseInt(hour), parseInt(minute))
              } else {
                dateObj = new Date(dateValue)
              }
            } else {
              dateObj = new Date(dateValue)
            }
            
            return format(dateObj, 'Pp', { locale: nlLocale })
          })(),
          practiceShort: (() => {
            const rawName: string =
              (patient.practiceName || patient.practice?.name || '').toString()
            const name = rawName.toLowerCase().trim()
            if (name.includes('slaapmakend')) return 'SLM'
            if (name.includes('mulder')) return 'MVS'
            return rawName ? rawName.substring(0, 3) : '-'
          })(),
          languageShort: patient.language === 'nl' ? 'NL' 
            : patient.language === 'en' ? 'ENG' 
            : patient.language ? patient.language.toUpperCase() : '-',
          locationShort: (() => {
            const locationParts = []
            if (patient.onsite === true) locationParts.push('LOC')
            if (patient.online === true) locationParts.push('ON')
            return locationParts.length > 0 ? locationParts.join(' / ') : '-'
          })()
        }
      })
  }, [debouncedSearchTerm])

  const openRecord = async (event: any, rowData: any) => {
    const programIds = await client.getSortedTreatmentPrograms(rowData.id)
    const programId = programIds[programIds.length - 1]
    navigate(`/clienten/${rowData.id}/trajecten/${programId}`)
  }

  const [searchResults, setSearchResults] = useState<any[]>([])
  const [isLoadingSearch, setIsLoadingSearch] = useState(false)

  useEffect(() => {
    const loadSearchResults = async () => {
      if (debouncedSearchTerm && serverSideSearch && onLoadMore && showPagination) {
        setIsLoadingSearch(true)
        try {
          const result = await onLoadMore({ 
            pageSize: 500, 
            page: 0, 
            search: debouncedSearchTerm.trim() 
          })
          const formattedData = formatPatientData(result.data)
          setSearchResults(formattedData)
          console.log('✅ LOADED ALL SEARCH RESULTS:', formattedData.length, 'patients for:', debouncedSearchTerm)
          console.log('First 3 results:', formattedData.slice(0, 3).map(p => p.fullName))
        } catch (error) {
          console.error('❌ Error loading search results:', error)
          setSearchResults([])
        } finally {
          setIsLoadingSearch(false)
        }
      } else {
        console.log('🧹 Clearing search results')
        setSearchResults([])
      }
    }

    loadSearchResults()
  }, [debouncedSearchTerm, serverSideSearch, onLoadMore, showPagination])

  const getData = async (query: any) => {
    if (debouncedSearchTerm && searchResults.length > 0) {
      return {
        data: searchResults,
        page: 0,
        totalCount: searchResults.length
      }
    }

    if (onLoadMore) {
      const result = await onLoadMore(query)
      const formattedData = formatPatientData(result.data)
      return {
        ...result,
        data: formattedData
      }
    }
    
    const filteredData = formatPatientData(patients).filter((patient) => {
      if (!debouncedSearchTerm) return true
      const fullName = getFullName(patient).toLowerCase()
      return fullName.includes(debouncedSearchTerm.toLowerCase())
    })
    
    const startIndex = query.page * query.pageSize
    const endIndex = startIndex + query.pageSize
    
    return {
      data: filteredData.slice(startIndex, endIndex),
      page: query.page,
      totalCount: filteredData.length
    }
  }

  const baseColumns = [
    {
      title: t('fullNameColumn'),
      field: 'fullName',
      render: (rowData: any) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {showSessionDot && (rowData as any).sessionsCount > 0 && typeof (rowData as any).lastSessionOlderThan2Months !== 'undefined' && (rowData as any).lastSessionOlderThan2Months !== null && (
            <Tooltip
              title={
                (rowData as any).lastSessionOlderThan2Months
                  ? 'Let op: laatste sessie was meer dan twee maanden geleden'
                  : 'Laatste sessie minder dan twee maanden geleden'
              }
              classes={{ tooltip: classes.warningTooltip }}
            >
              <FiberManualRecordIcon
                style={{
                  fontSize: 12,
                  marginRight: 8,
                  color: (rowData as any).lastSessionOlderThan2Months ? '#FF9800' : '#43A047'
                }}
              />
            </Tooltip>
          )}
          {rowData.fullName}
        </div>
      )
    },
    {
      title: (dateColumnTitle || t('shortBirthDateColumn')),
      field: 'shortBirthDate',
      render: (rowData: any) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span>{rowData.shortBirthDate || '-'}</span>
          {showIntakeWarnings && rowData.intakeWarning && (
            <Tooltip title={t('intakeOlderThan11MonthsWarning')} classes={{ tooltip: classes.warningTooltip }}>
              <WarningIcon className={classes.warningIcon} />
            </Tooltip>
          )}
        </div>
      )
    },
    ...(showSessionsColumn ? [{
      title: 'Sessies',
      field: 'sessionsCount',
      render: (rowData: any) => (rowData.sessionsCount && rowData.sessionsCount > 0 ? rowData.sessionsCount : '-')
    }] : [] as any),
    {
      title: 'Praktijk',
      field: 'practiceShort'
    },
    {
      title: 'Taal',
      field: 'languageShort'
    },
    {
      title: 'Locatie',
      field: 'locationShort'
    },
    {
      title: t('lastModifiedColumn'),
      field: 'formattedLastModifiedAt'
    }
  ]

  const filteredData = formatPatientData(patients)

  return (
    <Card className={classes.card}>
      <div className={classes.cardHeader}>
        <div
          className={classes.headerContent}
          role="button"
          tabIndex={0}
          onClick={handleExpandClick}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              handleExpandClick()
            }
          }}
        >
          <div className={classes.titleContainer}>
            <div className={classes.sectionIcon}>
              <IconComponent />
            </div>
            <Typography variant="h6">
              {title} ({totalCount})
            </Typography>
          </div>
          <IconButton
            className={`${classes.expandIcon} ${expanded ? classes.expandIconExpanded : ''}`}
            onClick={(e) => {
              e.stopPropagation()
              handleExpandClick()
            }}
            aria-expanded={expanded}
            aria-label={expanded ? "Collapse section" : "Expand section"}
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </div>
      </div>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <CardContent className={classes.cardContent}>
          {searchEnabled && (
            <TextField
              className={classes.searchField}
              placeholder={`Zoeken in ${title.toLowerCase()}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {isSearching ? (
                      <CircularProgress size={20} />
                    ) : (
                      <SearchIcon color={debouncedSearchTerm ? "primary" : "disabled"} />
                    )}
                  </InputAdornment>
                ),
                endAdornment: searchTerm ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSearchTerm('')
                        setSearchResults([]) 
                      }}
                      title="Wissen zoekterm"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                  ) : null
                }}
              />
          )}
          
          <div className={showPagination ? classes.tableContainer : classes.scrollableList}>
            {isTableVisible ? (
              <MaterialTable
                key={serverSideSearch ? `search-table-${tableKey}-${debouncedSearchTerm}` : undefined}
                title=""
                columns={baseColumns}
                data={(() => {
                  if (debouncedSearchTerm && serverSideSearch && showPagination) {
                    return searchResults
                  } else if (showPagination) {
                    return getData
                  } else {
                    return filteredData
                  }
                })()}
                isLoading={isLoadingSearch}
                icons={icons}
                options={{ 
                  search: false,
                  paging: showPagination,
                  pageSize: pageSize,
                  toolbar: false,
                  showTitle: false,
                  initialPage: 0,
                  pageSizeOptions: [10, 20, 50],
                  emptyRowsWhenPaging: false,
                  headerStyle: { color: '#6b7280' },
                  debounceInterval: 0
                }}
                actions={[
                  {
                    icon: () => <Folder color="disabled" />,
                    tooltip: t('tooltipText'),
                    onClick: openRecord
                  }
                ]}
                onRowClick={openRecord}
                localization={{
                  header: {
                    actions: ''
                  },
                  body: { 
                    emptyDataSourceMessage: isLoadingSearch 
                      ? 'Zoeken...' 
                      : t('emptyDataMessage') 
                  },
                  pagination: {
                    labelDisplayedRows: `{from}-{to} ${t('labelDisplayedRows')} {count}`,
                    labelRowsSelect: t('labelRowsSelected'),
                    labelRowsPerPage: t('labelRowsPerPage'),
                    firstAriaLabel: t('firstAria'),
                    firstTooltip: t('firstTooltip'),
                    previousAriaLabel: t('previousAria'),
                    previousTooltip: t('previousTooltip'),
                    nextAriaLabel: t('nextAria'),
                    nextTooltip: t('nextTooltip'),
                    lastAriaLabel: t('lastAria'),
                    lastTooltip: t('lastTooltip')
                  }
                }}
              />
            ) : (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                <CircularProgress />
              </div>
            )}
          </div>
        </CardContent>
      </Collapse>
    </Card>
  )
}
